import React, { FunctionComponent } from 'react';
import { InvestorEntryType, Money } from 'api';
import Translate from 'ui/atoms/translate';
import Number from 'ui/atoms/number';
import Currency from 'ui/atoms/currency';
import PlaceholderFallback from 'ui/atoms/placeholder-fallback';
import CopyButton from 'ui/molecules/copy-button';
import CardItem from 'src/ui/atoms/card-item';
import Button from 'ui/atoms/button';
import Hint from 'src/ui/atoms/hint';
import ActionButtons from 'src/ui/molecules/action-buttons';

export interface StudioInvestmentInvestorDetailsProps {
  /** Additional classes. */
  className?: string;
  name: string;
  email: string | null;
  investorId?: string;
  activeInvestmentsNo: number | null;
  activeInvestmentsSum: Money | null;
  distributionPlatform?: string;
  entryType?: InvestorEntryType;
  createdAt?: string;
  earliestDeletionDatetime?: string;
  isAccountDeactivated?: boolean;
  requestingUserEmail?: string;
  onReactivateAccount?: () => void;
}

const StudioInvestmentInvestorDetails: FunctionComponent<StudioInvestmentInvestorDetailsProps> = (props) => {
  const {
    email,
    investorId,
    activeInvestmentsNo,
    activeInvestmentsSum,
    distributionPlatform,
    entryType,
    createdAt,
    earliestDeletionDatetime,
    requestingUserEmail,
    isAccountDeactivated,
    onReactivateAccount,
  } = props;

  return (
    <>
      {investorId && (
        <CardItem
          title={<Translate name="studioInvestmentInvestorDetails.investorId" />}
          content={investorId}
          icon={<CopyButton valueToCopy={investorId} />}
        />
      )}
      <CardItem title={<Translate name="common.emailShort" />} content={email} />
      <CardItem
        title={<Translate name="common.investments" />}
        content={
          <PlaceholderFallback>
            {activeInvestmentsNo !== undefined && activeInvestmentsNo !== null && (
              <Number>{activeInvestmentsNo}</Number>
            )}
          </PlaceholderFallback>
        }
      />
      <CardItem
        title={<Translate name="studioInvestmentInvestorDetails.totalAmount" />}
        content={
          <PlaceholderFallback>
            {activeInvestmentsSum && <Currency>{activeInvestmentsSum}</Currency>}
          </PlaceholderFallback>
        }
      />
      {distributionPlatform && distributionPlatform.length > 0 && (
        <CardItem
          title={<Translate name="studioInvestmentInvestorDetails.distributionPlatform" />}
          content={<PlaceholderFallback>{distributionPlatform}</PlaceholderFallback>}
        />
      )}
      {investorId && (
        <CardItem
          title={<Translate name="studioInvestmentInvestorDetails.typeOfEntry" />}
          content={
            <Translate
              name={`studioInvestmentInvestorDetails.${
                entryType === InvestorEntryType.SINGLE_ENTRY ? 'singleEntry' : 'cumulativeEntry'
              }`}
            />
          }
        />
      )}
      {isAccountDeactivated && (
        <>
          <Hint variant="danger">
            <div>
              <strong>
                <Translate name="dashboardIssuerInvestorDetails.deleteAccount.deactivated" as="p" />
              </strong>
              <Translate
                name="dashboardIssuerInvestorDetails.futureDeletion.requestedContent1"
                args={[createdAt, requestingUserEmail]}
              />
              <Translate
                name="dashboardIssuerInvestorDetails.futureDeletion.requestedContent2"
                args={[earliestDeletionDatetime]}
              />
            </div>
          </Hint>
          <ActionButtons>
            <Button variant="secondary" onClick={onReactivateAccount}>
              <Translate name="dashboardIssuerInvestorDetails.activateAccount.title" />
            </Button>
          </ActionButtons>
        </>
      )}
    </>
  );
};

export default StudioInvestmentInvestorDetails;
